import React, { useCallback, useState, useEffect, useMemo, useContext, useRef } from 'react';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	handleKeyboardShortcutNavigation,
	ScrollBehavior,
	NavigationContext,
	ShortcutType,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import {
	focusElementAndUpdateTabIndex,
	getNextUnreadThreadKey,
} from '@confluence/comments-util/entry-points/domUtils.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams.ts';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass.ts';
import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
	AddFooterCommentMode,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import {
	useInlineCommentsContext,
	useInlineCommentsDispatchContext,
} from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	selectInlineComment,
	selectInlineCommentCompoundExperience,
} from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { withDialogs } from '@confluence/dialogs/entry-points/withDialogs.ts';
import { type DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	NEXT_COMMENT_SHORTCUT,
	PREV_COMMENT_SHORTCUT,
	UNIVERSAL_NEXT_UNREAD_COMMENT_SHORTCUT,
	UNIVERSAL_NEXT_COMMENT_SHORTCUT,
	UNIVERSAL_PREV_COMMENT_SHORTCUT,
	CREATE_COMMENT_SHORTCUT,
	UNIVERSAL_OPEN_COMMENT_EDITOR,
	UNIVERSAL_SELECT_COMMENT_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import {
	useCommentsPanel,
	useCommentsPanelState,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useObjectSidebarState } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { fg } from '@confluence/feature-gating';
import { useCreateCommentAction } from '@confluence/highlight-actions/entry-points/useCreateCommentAction.ts';
import {
	useCommentsDataState,
	CommentType,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { getNextRef, parsePanelAndInlineRefs } from './helper/commentsShortcutsHelper.ts';
import { useFooterComments } from './useFooterComments.ts';

type ViewCommentShortcutListenerProps = {
	isFabricPage: boolean;
	contentId?: string;
	dialogs: DialogsStateContainer;
	preloadedCanAddComments?: boolean;
};

const ViewCommentShortcutListenerComponent = ({
	isFabricPage,
	contentId,
	dialogs,
	preloadedCanAddComments,
}: ViewCommentShortcutListenerProps) => {
	const { navigateToInlineComment } = useInlineCommentsDispatchContext();
	const { activeHighlight } = useInlineCommentsContext();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { removeCommentQueryParams } = useInlineCommentQueryParams();
	const { orderedActiveAnnotationIdList } = useCommentsDataState();
	const { edition } = useSessionData();
	const { commentsDataMap } = useProcessedCommentsDataState();

	const emitter = getRendererAnnotationEventEmitter();
	const pageClass = usePageClass();
	const { showModal } = useDialogs();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isNextCommentLoading, setIsNextCommentLoading] = useState(false);

	const [
		{ currentlyRenderedThreads, currentlySelectedThreadKey },
		{ setCurrentlySelectedThreadKey },
	] = useCommentsPanel();

	// Track previous annotation for difference-based scrolling
	const previousAnnotationRef = useRef<string | undefined>(undefined);

	// Update ref whenever activeHighlight changes
	useEffect(() => {
		// Only update if there's a valid value (not empty string or null)
		if (activeHighlight) {
			previousAnnotationRef.current = activeHighlight;
		}
	}, [activeHighlight]);

	const { isObjectSidebarShown, panel } = useObjectSidebarState();
	const { expandCommentContainer, setFooterEditorEntry } = useObjectCommentDispatchContext();
	const { expanded, isObjectCommentWrapperVisible, footerEditorEntry } = useObjectCommentContext();
	const { addFooterCommentAdf } = useCommentsPanelState();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { unreadCommentIDSet } = useUnreadCommentsState();
	const { canAddComments } = useAddCommentPermissionCheck(
		contentId ?? '',
		false,
		preloadedCanAddComments,
	);

	const orderedActiveAnnotations = useMemo(() => {
		return orderedActiveAnnotationIdList.map((a) => a.threadKey);
	}, [orderedActiveAnnotationIdList]);

	useEffect(() => {
		setIsNextCommentLoading(false);
	}, [activeHighlight, currentlySelectedThreadKey]);

	useEffect(() => {
		return () => {
			setCurrentlySelectedThreadKey('');
		};
	}, [setCurrentlySelectedThreadKey]);

	const { footerCommentThreads, footerCommentIds } = useFooterComments();

	const { unreadInlineComments, unreadGeneralComments } = useMemo(() => {
		const unreadInlineComments: string[] = [];
		const unreadGeneralComments: string[] = [];

		orderedActiveAnnotations.forEach((threadKey) => {
			const commentData = commentsDataMap.inline[threadKey];
			if (commentData) {
				let isCommentUnread = false;
				let hasUnreadReplies = false;

				// Use unreadCommentIDSet when feature flag is enabled
				// in SSR, unreadCommentIDSet is undefined
				isCommentUnread = unreadCommentIDSet?.has(commentData.id);
				hasUnreadReplies =
					commentData.replies?.some((reply) => reply && unreadCommentIDSet?.has(reply.id)) || false;

				if (isCommentUnread || hasUnreadReplies) {
					unreadInlineComments.push(threadKey);
				}
			}
		});
		Object.keys(commentsDataMap.general).forEach((threadKey) => {
			const commentData = commentsDataMap.general[threadKey];
			if (commentData) {
				let isCommentUnread = false;
				let hasUnreadReplies = false;

				// Use unreadCommentIDSet when feature flag is enabled
				isCommentUnread = unreadCommentIDSet?.has(commentData.id);
				hasUnreadReplies =
					commentData.replies?.some((reply) => reply && unreadCommentIDSet?.has(reply.id)) || false;

				if (isCommentUnread || hasUnreadReplies) {
					unreadGeneralComments.push(threadKey);
				}
			}
		});

		return {
			unreadInlineComments,
			unreadGeneralComments,
		};
	}, [commentsDataMap, orderedActiveAnnotations, unreadCommentIDSet]);

	const sendKeyboardShortcutAnalytics = (shortcut: string) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'viewPage',
				action: 'pressed',
				actionSubject: 'keyboardShortcut',
				actionSubjectId: 'createComment',
				attributes: {
					key: shortcut,
				},
			},
		}).fire();
	};

	const { handleCommentButtonClick } = useCreateCommentAction({
		contentId: contentId || '',
		isFabric: isFabricPage,
		sendKeyboardShortcutAnalytics,
	});

	const getCommentIndex = useCallback(
		(currentThreadKey: string): number => {
			return [...orderedActiveAnnotations, ...footerCommentIds]?.findIndex(
				(threadKey) => threadKey === currentThreadKey,
			);
		},
		[orderedActiveAnnotations, footerCommentIds],
	);

	const openInlineComment = useCallback(
		(markerRef: string) => {
			if (pageClass === 'renderer' || pageClass === 'editor') {
				experienceTracker.start({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
					attributes: {
						annotationId: markerRef,
						entryPoint: 'comments-shortcuts/ViewCommentShortcutListener',
						pageClass: 'renderer',
					},
				});
			}
			const previousAnnotationId = previousAnnotationRef.current;
			setCurrentlySelectedThreadKey('');
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			const commentElement = document.getElementById(markerRef);
			selectInlineCommentCompoundExperience.addCommonAttributes({
				annotationId: markerRef,
			});
			selectInlineComment.debug(
				`Keyboard selection now triggering annotation selection for annotation ${markerRef}`,
			);
			emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
				annotationIds: [markerRef],
				eventTarget: commentElement,
			});
			handleKeyboardShortcutNavigation({
				scrollBehavior: ScrollBehavior.SMOOTH,
				block: 'center',
				annotationId: markerRef,
				previousAnnotationId,
				commentType: 'INLINE',
				isCommentsPanelOpen: isObjectSidebarShown,
				hasKeyPressed: true,
				context: NavigationContext.RENDERER,
			});
		},
		[emitter, setCurrentlySelectedThreadKey, experienceTracker, pageClass, isObjectSidebarShown],
	);

	const scrollToFooterComment = useCallback(
		(threadKey: string) => {
			// Reset previous annotation ref when navigating to footer comment
			previousAnnotationRef.current = undefined;
			setCurrentlySelectedThreadKey(threadKey);
			emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
				annotationIds: [],
				eventTarget: null,
			});
			handleKeyboardShortcutNavigation({
				scrollBehavior: ScrollBehavior.SMOOTH,
				block: 'center',
				annotationId: threadKey,
				commentType: 'FOOTER',
				isCommentsPanelOpen: isObjectSidebarShown,
				hasKeyPressed: true,
			});
		},
		[emitter, setCurrentlySelectedThreadKey, isObjectSidebarShown],
	);

	const onNavigationClick = useCallback(
		(nextMarkerRef: string) => {
			if (hasContentChanged) {
				showModal(CommentWarningDialog, {
					onConfirm: () => {
						resetContentChanged();
						openInlineComment(nextMarkerRef);
					},
				});
			} else {
				openInlineComment(nextMarkerRef);
			}
		},
		[hasContentChanged, resetContentChanged, showModal, openInlineComment],
	);

	const handlePanelCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			// If nothing is on the panel, there's nothing to navigate to
			if (currentlyRenderedThreads.length === 0) {
				return;
			}

			const { panelCommentRefs, navigableInlineCommentRefs } = parsePanelAndInlineRefs(
				currentlyRenderedThreads,
				orderedActiveAnnotations,
			);

			const nextRef = getNextRef(action, panelCommentRefs, currentlySelectedThreadKey);

			// Handle panel selection state management in the listener
			setCurrentlySelectedThreadKey(nextRef.ref);

			handleKeyboardShortcutNavigation({
				scrollBehavior: ScrollBehavior.SMOOTH,
				block: 'center',
				annotationId: nextRef.ref,
				commentType: nextRef.type === CommentType.GENERAL ? 'FOOTER' : 'INLINE',
				isCommentsPanelOpen: isObjectSidebarShown,
				hasKeyPressed: true,
				context: NavigationContext.RENDERER,
				isResolved: !navigableInlineCommentRefs.has(nextRef.ref),
			});
		},
		[
			currentlyRenderedThreads,
			orderedActiveAnnotations,
			currentlySelectedThreadKey,
			setCurrentlySelectedThreadKey,
			isObjectSidebarShown,
		],
	);

	const handleNextCommentNavigation = useCallback(
		({
			action,
			isNavigateToNextUnread,
		}: {
			isNavigateToNextUnread: boolean;
			action: 'next' | 'previous';
		}) => {
			//Check if the next inline comment is still in process of being rendered. Otherwise, quickly pressing keys will force comment to open and close instead of navigating
			if (isNextCommentLoading) return;

			setIsNextCommentLoading(true);

			navigateToInlineComment({
				action,
				triggeredByKeyboardShortcut: true,
				currentCommentIndex: getCommentIndex(activeHighlight || currentlySelectedThreadKey || ''),
				getCommentIndex,
				onNavigationClick,
				isEditor: false,
				removeCommentQueryParams,
				unresolvedInlineComments: orderedActiveAnnotations,
				createAnalyticsEvent,
				contentId,
				isNavigateToNextUnread,
				unreadInlineComments,
				unreadGeneralComments,
				footerComments: footerCommentIds,
				scrollToFooterComment,
				commentsDataMap,
			});
		},
		[
			isNextCommentLoading,
			navigateToInlineComment,
			getCommentIndex,
			activeHighlight,
			currentlySelectedThreadKey,
			onNavigationClick,
			removeCommentQueryParams,
			orderedActiveAnnotations,
			createAnalyticsEvent,
			contentId,
			unreadInlineComments,
			unreadGeneralComments,
			footerCommentIds,
			scrollToFooterComment,
			commentsDataMap,
		],
	);

	const handleStandaloneCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			if (activeHighlight || currentlySelectedThreadKey) {
				handleNextCommentNavigation({
					isNavigateToNextUnread: false,
					action,
				});
			} else {
				// No highlight selected, so we navigate to the first inline comment
				const firstInlineCommentMarkerRef = orderedActiveAnnotations[0];

				if (!firstInlineCommentMarkerRef && footerCommentThreads.length === 0) {
					return;
				}

				if (firstInlineCommentMarkerRef) {
					openInlineComment(firstInlineCommentMarkerRef);
				} else {
					scrollToFooterComment(footerCommentThreads[0]?.id);
				}
			}
		},
		[
			activeHighlight,
			currentlySelectedThreadKey,
			orderedActiveAnnotations,
			handleNextCommentNavigation,
			footerCommentThreads,
			openInlineComment,
			scrollToFooterComment,
		],
	);

	const handleCommentNavigation = useCallback(
		(action: 'next' | 'previous') => {
			// Define behavior when the comments panel is shown - panel FG is a prereq for nav, so has to be checked first
			if (isObjectSidebarShown && panel?.id === PanelName.CommentsPanel) {
				handlePanelCommentNavigation(action);
			} else {
				// Comments panel not shown
				handleStandaloneCommentNavigation(action);
			}
		},
		[
			isObjectSidebarShown,
			panel?.id,
			handlePanelCommentNavigation,
			handleStandaloneCommentNavigation,
		],
	);

	const handleNextUnreadNavigation = useCallback(() => {
		//unread panel navigation
		if (isObjectSidebarShown && panel?.id === PanelName.CommentsPanel) {
			let nextThreadKey = '';
			let nextCommentType = CommentType.GENERAL;
			if (!currentlySelectedThreadKey) {
				if (unreadInlineComments.length > 0) {
					nextThreadKey = unreadInlineComments[0];
					nextCommentType = CommentType.INLINE;
				} else {
					nextThreadKey = unreadGeneralComments[0];
				}
			} else {
				nextThreadKey =
					getNextUnreadThreadKey(
						getCommentIndex(currentlySelectedThreadKey ?? '') + 1, //Add one otherwise it might get stuck on the current comment because there are unread replies which are collapsed
						[...orderedActiveAnnotations, ...footerCommentIds],
						[...unreadInlineComments, ...unreadGeneralComments],
					) ?? '';
				if (unreadInlineComments.includes(nextThreadKey)) {
					nextCommentType = CommentType.INLINE;
				}
			}

			// Handle panel selection state management in the listener
			setCurrentlySelectedThreadKey(nextThreadKey);

			handleKeyboardShortcutNavigation({
				scrollBehavior: ScrollBehavior.SMOOTH,
				block: 'center',
				annotationId: nextThreadKey,
				commentType: nextCommentType === CommentType.GENERAL ? 'FOOTER' : 'INLINE',
				isCommentsPanelOpen: isObjectSidebarShown,
				hasKeyPressed: true,
				context: NavigationContext.RENDERER,
				shortcutType: ShortcutType.UNREAD,
			});
			return;
		}

		if (activeHighlight || currentlySelectedThreadKey) {
			handleNextCommentNavigation({
				isNavigateToNextUnread: true,
				action: 'next',
			});
		} else {
			if (unreadInlineComments.length + unreadGeneralComments.length === 0) {
				return;
			}
			if (unreadInlineComments[0]) {
				openInlineComment(unreadInlineComments[0]);
			} else {
				scrollToFooterComment(unreadGeneralComments[0]);
			}
		}
	}, [
		isObjectSidebarShown,
		panel?.id,
		handleNextCommentNavigation,
		unreadInlineComments,
		activeHighlight,
		openInlineComment,
		scrollToFooterComment,
		currentlySelectedThreadKey,
		unreadGeneralComments,
		footerCommentIds,
		getCommentIndex,
		orderedActiveAnnotations,
		setCurrentlySelectedThreadKey,
	]);

	const openCommentEditorShortcutCb = (e: KeyboardEvent) => {
		e.preventDefault();
		// This check is necessary to see which keyboard shortcut was used so it can be sent to analytics.
		// e.key is used to get the key that was pressed according to the current keyboard layout.
		// We compare with CREATE_COMMENT_SHORTCUT because it is a single-letter keyboard shortcut.
		// It's more difficult to compare with UNIVERSAL_OPEN_COMMENT_EDITOR because it includes modifiers.  Modifiers which change what e.key maps to depending on the keyboard layout.
		const shortcut =
			e.key === CREATE_COMMENT_SHORTCUT ? CREATE_COMMENT_SHORTCUT : UNIVERSAL_OPEN_COMMENT_EDITOR;

		if (canAddComments || !fg('confluence_fe_add_comment_kb_check_permission')) {
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			if (document.querySelector('[data-testid="highlightActionsPopup"]')) {
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				const createButton = document.querySelector('#create-inline-comment-button');
				// If createButton is not found or is disabled, do nothing.
				// This handles the case when hovering over media (image/video) objects in renderer.
				// In this scenario, a `highlightActionsPopup` is shown but the Comment button (which
				// has a different id) behaves differently: if any comment exists on the media object,
				// button click opens it as standalone; otherwise button click opens CreateComment.
				// Keyboard shortcut is disabled for this scenario.
				const createButtonIsEnabled = createButton && createButton?.getAttribute('disabled') !== '';

				if (createButtonIsEnabled) {
					handleCommentButtonClick({
						dialogs,
						shortcut,
						experienceAttributes: { entryPoint: 'keyboardShortcut', edition },
					});
				}
			} else {
				// Check if we should show sticky draft instead of scrolling to object comment wrapper
				const isShowStickyDraft =
					!isObjectCommentWrapperVisible &&
					(addFooterCommentAdf || expanded) &&
					footerEditorEntry !== AddFooterCommentMode.STICKY_DRAFT;

				if (!expanded) {
					expandCommentContainer({ entryPoint: 'keyboardShortcutView' });
				}

				if (isShowStickyDraft || !isObjectCommentWrapperVisible) {
					setFooterEditorEntry(AddFooterCommentMode.STICKY_DRAFT);
				} else {
					// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
					document
						.querySelector(`[data-testid='object-comment-wrapper']`)
						?.scrollIntoView({ behavior: 'smooth' });
				}
			}
		}

		sendKeyboardShortcutAnalytics(shortcut);
	};

	if (!isFabricPage) return null;

	return (
		<div data-testid="comments-shortcut-listener">
			<GeneralShortcutListener
				accelerator={UNIVERSAL_SELECT_COMMENT_SHORTCUT}
				listener={() => {
					const openComment =
						// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
						document.getElementById('renderer-comments-sidebar') ||
						// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
						document.getElementById(`comment-thread-${currentlySelectedThreadKey}-container`);
					if (openComment) {
						focusElementAndUpdateTabIndex(openComment);
					}
				}}
			/>
			<GeneralShortcutListener
				accelerator={UNIVERSAL_NEXT_COMMENT_SHORTCUT}
				deprecatedAccelerator={NEXT_COMMENT_SHORTCUT}
				listener={() => {
					selectInlineCommentCompoundExperience.selectAnnotation.start({
						pageClass,
						entryPoint: 'keyboard-navigation',
					});
					selectInlineComment.debug('keyboard navigation to next comment', {
						// whenTriggered props added to help debug on Hello (for reproducing issues)
						activeHighlightWhenTriggered: activeHighlight,
						currentlySelectedThreadKeyWhenTriggered: currentlySelectedThreadKey,
					});
					handleCommentNavigation('next');
				}}
			/>
			<GeneralShortcutListener
				accelerator={UNIVERSAL_PREV_COMMENT_SHORTCUT}
				deprecatedAccelerator={PREV_COMMENT_SHORTCUT}
				listener={() => {
					selectInlineCommentCompoundExperience.selectAnnotation.start({
						pageClass,
						entryPoint: 'keyboard-navigation',
					});
					selectInlineComment.debug('keyboard navigation to previous comment', {
						// whenTriggered props added to help debug on Hello (for reproducing issues)
						activeHighlightWhenTriggered: activeHighlight,
						currentlySelectedThreadKeyWhenTriggered: currentlySelectedThreadKey,
					});
					handleCommentNavigation('previous');
				}}
			/>
			<GeneralShortcutListener
				accelerator={UNIVERSAL_NEXT_UNREAD_COMMENT_SHORTCUT}
				listener={() => {
					selectInlineCommentCompoundExperience.selectAnnotation.start({
						pageClass,
						entryPoint: 'keyboard-navigation',
					});
					selectInlineComment.debug('Keyboard navigation to next unread comment', {
						// whenTriggered props added to help debug on Hello (for reproducing issues)
						activeHighlightWhenTriggered: activeHighlight,
						currentlySelectedThreadKeyWhenTriggered: currentlySelectedThreadKey,
					});
					handleNextUnreadNavigation();
				}}
			/>
			<GeneralShortcutListener
				deprecatedAccelerator={CREATE_COMMENT_SHORTCUT}
				accelerator={UNIVERSAL_OPEN_COMMENT_EDITOR}
				listener={(e) => openCommentEditorShortcutCb(e)}
			/>
		</div>
	);
};

export const ViewCommentShortcutListener: React.FC<
	Omit<ViewCommentShortcutListenerProps, 'dialogs'>
> = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(withDialogs(ViewCommentShortcutListenerComponent));
